const COLUMN_PALETTE = [
  { accent: "#2a3c7d", soft: "#eef1f9" },
  { accent: "#0d6efd", soft: "#e8f1ff" },
  { accent: "#198754", soft: "#e8f6ee" },
  { accent: "#fd7e14", soft: "#fff3e8" },
  { accent: "#6f42c1", soft: "#f3eefb" },
  { accent: "#20c997", soft: "#e8faf5" },
  { accent: "#dc3545", soft: "#fdecee" },
  { accent: "#6c757d", soft: "#f1f3f5" },
];

const TERMINAL_KEYWORDS = [
  "reject",
  "declined",
  "dropped",
  "withdrawn",
  "not interested",
  "duplicate",
  "abscond",
];

const PIPELINE_HINT_KEYWORDS = [
  "new",
  "submit",
  "added",
  "sourced",
  "shortlist",
  "l1",
  "l2",
  "l3",
  "level 1",
  "level 2",
  "level 3",
  "round",
  "client",
  "final",
  "select",
  "cleared",
  "offer",
  "offered",
  "join",
  "onboard",
  "deployed",
  "started",
];

function normalizeStatusName(name) {
  return String(name || "").trim().toLowerCase();
}

export function getKanbanColumnTheme(index) {
  return COLUMN_PALETTE[index % COLUMN_PALETTE.length];
}

export function groupCandidatesByStatus(candidates = [], statuses = []) {
  const groups = {};
  const statusIds = new Set();

  statuses.forEach((status) => {
    const id = String(status._status_id);
    groups[id] = [];
    statusIds.add(id);
  });

  groups.unassigned = [];

  candidates.forEach((candidate) => {
    const statusId = candidate.candidate_status_id;
    const key =
      statusId != null && statusId !== "" && groups[String(statusId)]
        ? String(statusId)
        : "unassigned";
    groups[key].push(candidate);
  });

  return { groups, statusIds };
}

export function isTerminalCandidateStatus(status) {
  const name = normalizeStatusName(status?.status_name);
  if (!name) return false;
  return TERMINAL_KEYWORDS.some((keyword) => name.includes(keyword));
}

export function isLikelyPipelineStatus(status) {
  if (Number(status?.status_show_in_kanban) === 1) {
    return true;
  }

  if (
    status?.status_show_in_kanban != null &&
    status?.status_show_in_kanban !== "" &&
    Number(status.status_show_in_kanban) === 0
  ) {
    return false;
  }

  const name = normalizeStatusName(status?.status_name);
  if (!name || isTerminalCandidateStatus(status)) {
    return false;
  }

  return PIPELINE_HINT_KEYWORDS.some((keyword) => name.includes(keyword));
}

export function getPipelineStatuses(statuses = []) {
  const flagged = statuses.filter((status) => Number(status.status_show_in_kanban) === 1);

  let pipeline = flagged.length > 0 ? flagged : statuses.filter(isLikelyPipelineStatus);

  if (pipeline.length === 0) {
    pipeline = statuses.filter((status) => !isTerminalCandidateStatus(status)).slice(0, 8);
  }

  return [...pipeline].sort((a, b) => {
    const orderA = Number(a.status_kanban_order) || 9999;
    const orderB = Number(b.status_kanban_order) || 9999;
    if (orderA !== orderB) {
      return orderA - orderB;
    }

    return statuses.indexOf(a) - statuses.indexOf(b);
  });
}

export function buildKanbanColumns(statuses = [], groups = {}) {
  const pipelineStatuses = getPipelineStatuses(statuses);
  const pipelineIds = new Set(pipelineStatuses.map((status) => String(status._status_id)));

  const columns = pipelineStatuses.map((status, index) => ({
    id: String(status._status_id),
    label: status.status_name,
    schedulesInterview: Number(status.status_schedule_interview) === 1,
    theme: getKanbanColumnTheme(index),
    cards: groups[String(status._status_id)] || [],
    isPipeline: true,
    isOther: false,
  }));

  if ((groups.unassigned || []).length > 0) {
    columns.unshift({
      id: "unassigned",
      label: "Unassigned",
      schedulesInterview: false,
      theme: getKanbanColumnTheme(columns.length),
      cards: groups.unassigned,
      isPipeline: false,
      isOther: false,
    });
  }

  const otherCards = [];
  statuses.forEach((status) => {
    const id = String(status._status_id);
    if (pipelineIds.has(id)) {
      return;
    }
    (groups[id] || []).forEach((candidate) => otherCards.push(candidate));
  });

  if (otherCards.length > 0) {
    columns.push({
      id: "other",
      label: "Other statuses",
      schedulesInterview: false,
      theme: getKanbanColumnTheme(columns.length),
      cards: otherCards,
      isPipeline: false,
      isOther: true,
    });
  }

  return columns;
}

export function groupStatusesForMenu(statuses = []) {
  const groups = {
    pipeline: { label: "Pipeline", items: [] },
    interview: { label: "Schedules interview", items: [] },
    terminal: { label: "Closed / other", items: [] },
  };

  statuses.forEach((status) => {
    if (Number(status.status_schedule_interview) === 1) {
      groups.interview.items.push(status);
      return;
    }

    if (isTerminalCandidateStatus(status)) {
      groups.terminal.items.push(status);
      return;
    }

    groups.pipeline.items.push(status);
  });

  return groups;
}

export function getCandidateCurrentStatusId(candidate) {
  return String(candidate?.candidate_status_id ?? "unassigned");
}

export function buildCandidateKanbanUpdatePayload(candidate, newStatusId, extraComments = "") {
  if (!candidate?._candidate_id) return null;

  return {
    candidate_firstname: candidate.candidate_firstname || "",
    candidate_lastname: candidate.candidate_lastname || "",
    candidate_client_id: candidate.candidate_client_id || "",
    candidate_requirement_id: candidate.candidate_requirement_id || "",
    candidate_mobile: candidate.candidate_mobile || "",
    candidate_email: candidate.candidate_email || "",
    candidate_experience_total: candidate.candidate_experience_total || "",
    candidate_experience_relevant: candidate.candidate_experience_relevant || "",
    candidate_present_location: candidate.candidate_present_location || "",
    candidate_preffered_location: candidate.candidate_preffered_location || "",
    candidate_current_ctc: candidate.candidate_current_ctc || "",
    candidate_offered_ctc: candidate.candidate_offered_ctc || "",
    candidate_education: candidate.candidate_education || "",
    candidate_current_company: candidate.candidate_current_company || "",
    candidate_notice_period: candidate.candidate_notice_period || "",
    candidate_remarks: candidate.candidate_remarks || "",
    candidate_resume_id: candidate.candidate_resume_id || "",
    candidate_status_id: newStatusId,
    candidate_comments:
      extraComments ||
      candidate.candidate_comments ||
      "",
  };
}

export function getCandidateDisplayName(candidate) {
  return `${candidate?.candidate_firstname || ""} ${candidate?.candidate_lastname || ""}`.trim() || "Unnamed candidate";
}
